<template>
  <div class="modal fade p-0" id="addInventoryExtra" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="d-flex flex-wrap w-100 position-relative event-auto">
        <div class="card shadow-none w-100">
          <Form
            ref="myForm"
            class="custom-form mt-2 assign-shops"
            :validation-schema="schema"
            @submit="onSubmit"
            @keydown="errors_v.clear($event.target.name)"
          >
            <div class="card-header card-header-primary d-flex flex-wrap align-items-center position-relative">
              <div class="content-card-left w-100">
                <h4 class="card-title text-white fs-4">Add Extra Inventory</h4>
                <p class="fw-light text-white mb-0 pt-1">Capture additional stock for an existing product</p>
              </div>
            </div>

            <div class="card-body">
              <div class="row">
                <div class="col-12 mb-4">
                  <div class="position-relative form-group">
                    <Field
                      as="select"
                      name="company_id"
                      class="form-select custom-select"
                      v-model="company_model"
                      @click="selectFocus = true"
                    >
                      <option value="0">Choose Company</option>
                      <option v-for="company in companies" :key="company.id" :value="company.id">
                        {{ company.name }}
                      </option>
                    </Field>
                    <label :class="[selectFocus ? 'focus' : '', 'position-absolute select-label']">Company</label>
                    <i class="bi bi-caret-down-fill select-arrow position-absolute"></i>
                    <ErrorMessage class="invalid-message mb-0 pt-2" name="company_id" />
                    <p
                      v-if="errors_v.get('companyId') && (!company_model || company_model === 0)"
                      class="invalid-message mb-0 pt-2"
                    >
                      {{ errors_v.get('companyId') }}
                    </p>
                  </div>
                </div>

                <div class="col-12 mb-4">
                  <v-date-picker
                    class="custom-calendar custom-single-calendar left-caret-none"
                    v-model="inventoryDate"
                    :masks="{ input: 'DD/MM/YY' }"
                  >
                    <template #default="{ inputValue, inputEvents }">
                      <div class="position-relative calendar-input-wrapper clearfix w-100">
                        <span class="d-inline-block me-2 float-left">Date</span>
                        <i class="bi bi-calendar3 position-absolute icon-left-placement"></i>
                        <input class="icon-left position-relative float-left w-100" :value="inputValue" v-on="inputEvents" />
                      </div>
                    </template>
                  </v-date-picker>
                </div>

                <div class="col-lg-6 col-md-6 col-12 mb-4">
                  <div class="position-relative form-group">
                    <Multiselect
                      id="extra_product_select"
                      v-model="product_model"
                      :options="productOptions"
                      class="custom-multiselect"
                      placeholder="Choose Product"
                      :searchable="true"
                      :showOptions="true"
                      mode="single"
                      label="name"
                      trackBy="name"
                      valueProp="id"
                    />
                    <Field type="hidden" name="product_id" v-model="product_model" />
                    <ErrorMessage class="invalid-message mb-0 pt-2" name="product_id" />
                    <p
                      v-if="errors_v.get('productId') && (!product_model || product_model === 0)"
                      class="invalid-message mb-0 pt-2"
                    >
                      {{ errors_v.get('productId') }}
                    </p>
                  </div>
                </div>

                <div class="col-lg-3 col-md-6 col-12 mb-4">
                  <div class="position-relative form-group">
                    <Field
                      as="select"
                      name="packing_type"
                      class="form-select custom-select"
                      v-model="packing_type"
                      @click="selectFocus = true"
                    >
                      <option value="boxes">Boxes</option>
                      <option value="cartons">Cartons</option>
                    </Field>
                    <label :class="[selectFocus ? 'focus' : '', 'position-absolute select-label']">Quantity Type</label>
                    <i class="bi bi-caret-down-fill select-arrow position-absolute"></i>
                    <ErrorMessage class="invalid-message mb-0 pt-2" name="packing_type" />
                  </div>
                </div>

                <div class="col-lg-3 col-md-6 col-12 mb-4">
                  <div class="position-relative">
                    <Field
                      type="number"
                      min="0"
                      step="any"
                      name="quantity"
                      :class="[quantity === '' ? '' : 'active', 'border-input position-relative w-100']"
                      v-model="quantity"
                    />
                    <label class="mb-0 placeholder-label w-auto">Quantity</label>
                    <ErrorMessage class="invalid-message mb-0 pt-2" name="quantity" />
                    <p v-if="errors_v.get('quantity')" class="invalid-message mb-0 pt-2">
                      {{ errors_v.get('quantity') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-footer px-4 pb-0 border-0 bg-transparent mb-3">
              <button class="btn btn-primary w-100" :disabled="searchDisabled">
                <div v-if="loader" class="spinner-border me-2" role="status"></div>
                Add Extra Quantity
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Form, Field, ErrorMessage } from 'vee-validate';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';
import { ref, reactive, onMounted, watch } from 'vue';
import * as yup from 'yup';
import CompanyServices from '@/services/CompanyServices';
import ProductServices from '@/services/ProductServices';
import InventoryServices from '@/services/InventoryServices';
import Errors from '@/utils/Errors';

export default {
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  emits: ['inventory-extra-added'],
  setup(_, { emit }) {
    const selectFocus = ref(false);
    const companies = ref([]);
    const productOptions = ref([]);
    const company_model = ref(0);
    const product_model = ref(null);
    const packing_type = ref('boxes');
    const quantity = ref('');
    const inventoryDate = ref(new Date());
    const loader = ref(false);
    const searchDisabled = ref(false);
    const errors_v = reactive(new Errors());
    const myForm = ref(null);

    const schema = yup.object({
      company_id: yup.number().moreThan(0, 'Company is required'),
      product_id: yup.number().moreThan(0, 'Product is required'),
      packing_type: yup.string().required('Packing type is required'),
      quantity: yup
        .number()
        .typeError('Only numbers allowed')
        .moreThan(0, 'Quantity must be greater than zero'),
    });

    const formatDateYMD = (dateValue) => {
      try {
        if (!dateValue) return '';
        const dt = new Date(dateValue);
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const d = String(dt.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      } catch (e) {
        return '';
      }
    };

    const resetForm = () => {
      company_model.value = 0;
      product_model.value = null;
      packing_type.value = 'boxes';
      quantity.value = '';
      inventoryDate.value = new Date();
      productOptions.value = [];
      errors_v.record({});
      if (myForm.value && typeof myForm.value.resetForm === 'function') {
        myForm.value.resetForm({
          values: {
            company_id: 0,
            product_id: null,
            packing_type: 'boxes',
            quantity: '',
          },
        });
      }
    };

    const closeModal = () => {
      try {
        const modalEl = document.getElementById('addInventoryExtra');
        if (!modalEl) return;
        let modalInstance = window.bootstrap?.Modal?.getInstance(modalEl);
        if (!modalInstance && window.bootstrap?.Modal) {
          modalInstance = new window.bootstrap.Modal(modalEl);
        }
        modalInstance?.hide();
      } catch (e) {
        /* ignore bootstrap issues */
      }
    };

    const onSubmit = async (values) => {
      loader.value = true;
      searchDisabled.value = true;
      errors_v.record({});

      const payload = {
        companyId: values.company_id,
        productId: values.product_id,
        packingType: values.packing_type,
        quantity: values.quantity,
        date: formatDateYMD(inventoryDate.value),
      };

      try {
        await InventoryServices.addExtraInventory(payload);
        resetForm();
        closeModal();
        emit('inventory-extra-added');
      } catch (error) {
        if (error?.response?.status === 422) {
          errors_v.record(error.response.data.errors || {});
        }
      } finally {
        loader.value = false;
        searchDisabled.value = false;
      }
    };

    onMounted(async () => {
      try {
        const response = await CompanyServices.paginateCompanies();
        companies.value = response.data.data;
      } catch (e) {
        companies.value = [];
      }
    });

    watch(company_model, async (current, previous) => {
      if (current && current !== previous) {
        try {
          const response = await ProductServices.CompanyProducts(current);
          productOptions.value = response?.data || [];
        } catch (e) {
          productOptions.value = [];
        }
        product_model.value = null;
        if (myForm.value && typeof myForm.value.setFieldValue === 'function') {
          myForm.value.setFieldValue('product_id', null);
        }
      }
    });

    return {
      selectFocus,
      companies,
      productOptions,
      company_model,
      product_model,
      packing_type,
      quantity,
      inventoryDate,
      loader,
      searchDisabled,
      errors_v,
      myForm,
      schema,
      onSubmit,
      resetForm,
      closeModal,
    };
  },
};
</script>
